import React, { useRef, useEffect } from "react";

// Hero animation replica of the original ideamedia.org slider effect:
// the photo underneath is covered by a white layer, while soft moving
// circles "cut through" the layer revealing the image in transparency.
const ParticleHero = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const parent = canvas.parentElement;
    let animationId;
    let circles = [];
    const mouse = { x: null, y: null };

    // white cover layer color (same tone as the original light background)
    const COVER = "rgba(240, 241, 242, 0.94)";

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();

    const circleCount = () =>
      Math.min(26, Math.max(12, Math.floor((canvas.width * canvas.height) / 90000)));

    const makeCircle = () => {
      const angle = Math.random() * Math.PI * 2;
      const speed = 0.15 + Math.random() * 0.35;
      return {
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        r: 30 + Math.random() * 110, // circle radius
        // how much of the photo shows through (0 = none, 1 = fully)
        reveal: 0.35 + Math.random() * 0.45,
        // gentle radius pulsing
        pulse: Math.random() * Math.PI * 2,
        pulseSpeed: 0.004 + Math.random() * 0.008,
      };
    };

    const init = () => {
      circles = Array.from({ length: circleCount() }, makeCircle);
    };
    init();

    const draw = () => {
      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);

      // 1) white layer covering the whole photo
      ctx.globalCompositeOperation = "source-over";
      ctx.fillStyle = COVER;
      ctx.fillRect(0, 0, w, h);

      // 2) punch soft transparent circles through the layer
      ctx.globalCompositeOperation = "destination-out";

      for (const c of circles) {
        c.x += c.vx;
        c.y += c.vy;

        // magnet effect: nearby circles get gently pulled toward the cursor
        if (mouse.x !== null) {
          const dx = mouse.x - c.x;
          const dy = mouse.y - c.y;
          const dist = Math.hypot(dx, dy);
          const MAGNET_DIST = 380;
          if (dist < MAGNET_DIST && dist > 1) {
            const force = (1 - dist / MAGNET_DIST) * 0.045;
            c.x += dx * force;
            c.y += dy * force;
          }
        }

        c.pulse += c.pulseSpeed;
        const r = c.r * (0.85 + 0.15 * Math.sin(c.pulse));

        // bounce softly on edges (with radius margin)
        if (c.x < -c.r) c.x = w + c.r;
        if (c.x > w + c.r) c.x = -c.r;
        if (c.y < -c.r) c.y = h + c.r;
        if (c.y > h + c.r) c.y = -c.r;

        // soft-edged hole: radial gradient from reveal alpha to 0
        const g = ctx.createRadialGradient(c.x, c.y, 0, c.x, c.y, r);
        g.addColorStop(0, `rgba(0, 0, 0, ${c.reveal})`);
        g.addColorStop(0.7, `rgba(0, 0, 0, ${c.reveal * 0.55})`);
        g.addColorStop(1, "rgba(0, 0, 0, 0)");
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(c.x, c.y, r, 0, Math.PI * 2);
        ctx.fill();
      }

      // 3) extra reveal circle that follows the mouse
      if (mouse.x !== null) {
        const r = 180;
        const g = ctx.createRadialGradient(mouse.x, mouse.y, 0, mouse.x, mouse.y, r);
        g.addColorStop(0, "rgba(0, 0, 0, 0.6)");
        g.addColorStop(0.7, "rgba(0, 0, 0, 0.3)");
        g.addColorStop(1, "rgba(0, 0, 0, 0)");
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(mouse.x, mouse.y, r, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.globalCompositeOperation = "source-over";
      animationId = requestAnimationFrame(draw);
    };
    draw();

    const onMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
    };
    const onMouseLeave = () => {
      mouse.x = null;
      mouse.y = null;
    };
    const onResize = () => {
      resize();
      init();
    };

    parent.addEventListener("mousemove", onMouseMove);
    parent.addEventListener("mouseleave", onMouseLeave);
    window.addEventListener("resize", onResize);

    return () => {
      cancelAnimationFrame(animationId);
      parent.removeEventListener("mousemove", onMouseMove);
      parent.removeEventListener("mouseleave", onMouseLeave);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return <canvas ref={canvasRef} className="idm-particles" aria-hidden="true" />;
};

export default ParticleHero;
